import { toast } from 'sonner';

import { electronApis } from '@/features/electron-api';
import { userStore } from '@/stores/user.store';

import { rememberAppMention } from './app-mention-memory';

import type { MentionedApp } from './app-source';

// Apps whose windows aren't individually addressable, so context capture has to
// traverse every window of the process instead of a single windowId.
const TRAVERSE_ALL_WINDOWS_BUNDLE_IDS = new Set(['com.tinyspeck.slackmacgap']);

export const escapeXmlText = (value: string): string =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

const escapeXmlAttribute = (value: string): string =>
  escapeXmlText(value).replaceAll('"', '&quot;').replaceAll("'", '&#39;');

export const captureAppContextPayload = (app: MentionedApp) =>
  electronApis.hummingbird.captureAppContext({
    pid: app.pid,
    windowId: app.windowId ?? undefined,
    traverseAllWindows:
      app.windowId === null &&
      app.bundleId !== null &&
      TRAVERSE_ALL_WINDOWS_BUNDLE_IDS.has(app.bundleId),
  });

export const copyAppContextForDebug = async (
  app: MentionedApp
): Promise<void> => {
  if (!userStore.isInternalUser) return;

  const payload = await captureAppContextPayload(app);
  if (!payload.content) {
    toast.error(`No context available for ${app.name}.`);
    return;
  }

  await navigator.clipboard.writeText(payload.content);
};

type CapturedAppContexts = {
  content: string;
  failedAppNames: string[];
};

/**
 * Capture each mentioned app's on-screen context as `<context app="…">…</context>`
 * blocks. De-dupes by window (or process, for window-less apps). Throws only when
 * every app fails; partial failures are reported via `failedAppNames`.
 */
export const captureAppContexts = async (
  apps: MentionedApp[],
  {
    onAppCaptureFailed,
  }: { onAppCaptureFailed?: (app: MentionedApp) => void } = {}
): Promise<CapturedAppContexts> => {
  apps.forEach(({ name, bundleId, iconUrl }) => {
    rememberAppMention({ name, bundleId, iconUrl });
  });

  const uniqueApps = [
    ...new Map(
      apps.map((app) => [
        app.windowId === null ? `app:${app.pid}` : `window:${app.windowId}`,
        app,
      ])
    ).values(),
  ];
  const captureApp = async (app: MentionedApp) => {
    const payload = await captureAppContextPayload(app);
    if (!payload.content) {
      onAppCaptureFailed?.(app);
      throw new Error(`No context available for ${app.name}`);
    }
    return `<context app="${escapeXmlAttribute(app.name)}">${escapeXmlText(payload.content)}</context>`;
  };

  const results = await Promise.allSettled(uniqueApps.map(captureApp));
  const content: string[] = [];
  const failedAppNames: string[] = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      content.push(result.value);
    } else {
      failedAppNames.push(uniqueApps[index].name);
    }
  });

  if (uniqueApps.length > 0 && content.length === 0) {
    throw new Error('Could not capture context from any selected app');
  }

  return { content: content.join('\n'), failedAppNames };
};
